import { PriceTag } from "@/components/PriceTag";
import { imgPos } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listRelatedProducts } from "@/lib/api";
import { useT } from "@/context/LanguageContext";
import ProgressiveImage from "@/components/ProgressiveImage";

/**
 * RelatedProducts — a horizontal strip of same-category products, sorted
 * by closest price to the anchor product. Rendered at the bottom of PDP.
 */
const RelatedProducts = ({ slug }) => {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listRelatedProducts(slug, 6)
      .then((data) => {
        if (!cancelled) {
          setItems(data);
          setLoaded(true);
        }
      })
      .catch(() => setLoaded(true));
    return () => { cancelled = true; };
  }, [slug]);

  if (!loaded || items.length === 0) return null;

  return (
    <section
      data-testid="related-products"
      className="mt-24 md:mt-32 border-t border-[#F1DCC5] pt-16 md:pt-20 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <h2
            data-testid="related-title"
            className="font-serif-display text-3xl md:text-5xl tracking-tight text-[#241C18]"
          >
            {t("related.title")}
          </h2>
        </div>

        <div className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6 md:mx-0 md:px-0 md:grid md:grid-cols-4">
          {items.map((p) => (
            <Link
              key={p.id}
              to={`/product/${p.slug}`}
              data-testid={`related-item-${p.slug}`}
              className="group snap-start shrink-0 w-[70vw] max-w-[280px] md:w-auto md:max-w-none"
            >
              <div className="aspect-[3/4] overflow-hidden bg-[#F9F1E4]">
                <ProgressiveImage
                  src={p.images?.[0]}
                  alt={p.name}
                  position={imgPos(p, p.images?.[0])}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="mt-3 flex items-baseline justify-between gap-3">
                <div className="font-serif-display text-base md:text-lg text-[#241C18] leading-tight">{p.name}</div>
                <div className="font-badge text-[11px] tracking-[0.12em] text-[#241C18] shrink-0">
                  <PriceTag product={p} showPercent={false} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedProducts;
